import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import styled from 'styled-components';
import { useStore } from '../../hooks/storeContext';
import { ROUTES } from '../../constants/routes';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f5f5f5;
`;

const RequirePermission = observer(({ children, permission }) => {
  const { authStore } = useStore();
  const location = useLocation();
  const token = localStorage.getItem('bhaAuthToken');
  // Only show the verifying screen when the user still needs to be fetched.
  // Otherwise it flashes a full-page light background on every navigation.
  const [loading, setLoading] = useState(() => !!token && !authStore.me);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      if (authStore.me) {
        setLoading(false);
        return;
      }

      try {
        await authStore.whoami();
      } catch (error) {
        // whoami handles 401 cleanup internally — don't force a full logout
        // on transient errors (network blips, timeouts, etc.)
        console.error('Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [authStore, token]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Verifying access..." />
      </LoadingContainer>
    );
  }

  // No token - redirect to login
  if (!token) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // No user data - redirect to login
  if (!authStore.me) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Check if user has the required permission (or is admin)
  if (!authStore.hasPermission(permission)) {
    // Instead of logging out, redirect to login with error message
    return <Navigate to={ROUTES.LOGIN} state={{ 
      from: location, 
      error: 'You do not have permission to access this page.' 
    }} replace />;
  }

  // User has permission - render protected component
  return children;
});

export default RequirePermission;
